import { ITheme } from "../core/containers/Theme/Common/Theme";
import { IFreeSpinsState } from "../../src/v2/redux/reducers/FreeSpins.reducer";
import { IFreeRoundsState } from "../../src/v2/redux/reducers/FreeRounds.reducer";

export enum View {
  DEFAULT = "DEFAULT",
  AUTOPLAY_SETTINGS = "AUTOPLAY_SETTINGS",
  BET_SETTINGS = "BET_SETTINGS",
  MAIN_SETTINGS = "MAIN_SETTINGS",
  PAYTABLE = "PAYTABLE",
  GAME_RULES = "GAME_RULES",
  GAME_HISTORY = "GAME_HISTORY"
}

export enum SpinButtonPosition {
  DEFAULT = "DEFAULT",
  DOCKED = "DOCKED"
}

export enum DisplayMode {
  DEFAULT = "DEFAULT",
  MINIMAL = "MINIMAL",
  TOPBAR = "TOPBAR"
}

export enum MoneyType {
  CASH = "CASH",
  COINS = "COINS"
}

export enum Target {
  DESKTOP = "DESKTOP",
  MOBILE = "MOBILE"
}

export interface IPlayUIAPI {
  setState: (state: IPlayUIState) => void;
  onMoneyTypeChangeRequest(func: (moneyType: MoneyType) => void): void;
  onAutoplaySettingChangeRequest(
    func: (setting: { id: string; active: boolean; attributes: object }) => void
  ): void;
  onAutoplayResetRequest(func: () => void): void;
  onAutoplaySelectRoundsChangeRequest(func: (rounds: number) => void): void;
  onBetDenominationChangeRequest(func: (denomination: number) => void): void;
  onBetLevelChangeRequest(func: (level: number) => void): void;
  onGameRulesRequest(func: () => void): void;
  onGameHistoryRequest(func: () => void): void;
  onHomeRequest(func: () => void): void;
  onViewChangeRequest(func: (view: View) => void): void;
  onPluginRequest(func: () => void): void;
  onSettingChangeRequest(
    func: (setting: { key: string; value: boolean }) => void
  ): void;
  onSpinRequest(func: () => void): void;
  onTickerAnimationEnd(func: () => void): void;
  onVolumeChangeRequest(func: (volume: number) => void): void;
  onMuteChangeRequest(func: (muted: boolean) => void): void;
  onQuickSpinChangeRequest(func: (value: boolean) => void): void;
}

interface IActivityIndicatorState {
  enabled?: boolean;
}

interface IPlayForFunIndicatorState {
  enabled?: boolean;
}

interface IAutoplaySettingState {
  id?: string;
  text?: string;
  disabled?: boolean;
  active?: boolean;
  attributes?: {
    label?: string;
    value?: number;
    required?: boolean;
  };
}

interface IAutoplayState {
  enabled?: boolean;
  rounds?: {
    values?: number[];
    selected?: number | null;
    left?: number;
  };
  running?: boolean;
  settings?: IAutoplaySettingState[];
}

interface IBetState {
  enabled?: boolean;
  size?: number;
  level?: number;
  levels?: number[];
  denominations?: number[];
  denomination?: number;
  valueInCoins?: number;
  valueInCash?: number;
}

interface IClockState {
  date?: Date;
}

interface ICurrencyState {
  iso?: string;
  symbol?: string;
  decimals?: number;
  decimalDivider?: string;
  thousandsDivider?: string;
  coinsDivider?: string;
  showCurrencyChar?: boolean;
}

interface IDialogMessageFragment {
  text: string;
  url?: string;
}

interface IDialogOptions {
  title: string;
  message?: string | IDialogMessageFragment[];
  centerMessage?: boolean;
  cover?: boolean;
  buttons?: {
    primary?: boolean;
    label: string;
    callback: () => void;
  }[];
}

interface IDialogState {
  enabled: boolean;
  options?: IDialogOptions;
}

interface IGameHistoryState {
  enabled?: boolean;
}

interface IGameRulesState {
  content?: string;
}

interface IHomeState {
  enabled?: boolean;
}

interface IMenuState {
  enabled?: boolean;
}

export interface IPaytableState {
  content?: string;
}

interface IPluginState {
  enabled?: boolean;
  icon?: string;
}

interface IQuickSpinState {
  enabled?: boolean;
  active?: boolean;
}

interface ISpinState {
  enabled?: boolean;
  spinning?: boolean;
  buttonPosition?: SpinButtonPosition;
}

interface ITickerMessageFragment {
  text: string;
  url?: string;
}

interface ITickerState {
  enabled?: boolean;
  message?: string | ITickerMessageFragment[];
  animate?: boolean;
}

interface IVolumeState {
  enabled?: boolean;
  muted?: boolean;
  value?: number;
}

interface ISettingState {
  key?: string;
  value?: boolean;
  name?: string;
}

interface ISettingsState {
  settings?: ISettingState[];
}

interface IBalanceState {
  valueInCash?: number;
  valueInCoins?: number;
}

interface IWinState {
  valueInCoins?: number;
  valueInCash?: number;
  denomination?: number;
}

interface ITotalWinState {
  valueInCoins?: number;
  valueInCash?: number;
  denomination?: number;
}

interface ILeftHandModeState {
  value?: boolean;
}

interface IThemeState extends ITheme {}

interface IGameInformationState {
  gameProducer?: string;
  gameName?: string;
  gameClientVersion?: string;
  gameServerVersion?: string;
  maxBet?: number;
  minBet?: number;
}

interface IUiBlockerState {
  enabled: boolean;
}

export interface IPlayUIState {
  // Properties
  displayMode?: DisplayMode;
  gameInformation?: IGameInformationState;
  moneyType?: MoneyType;
  moneyTypes?: MoneyType[];
  view?: View;

  // Features
  activityIndicator?: IActivityIndicatorState;
  autoplay?: IAutoplayState;
  balance?: IBalanceState;
  bet?: IBetState;
  clock?: IClockState;
  currency?: ICurrencyState;
  dialog?: IDialogState;
  freeRounds?: IFreeRoundsState;
  freeSpins?: IFreeSpinsState;
  gameHistory?: IGameHistoryState;
  gameRules?: IGameRulesState;
  home?: IHomeState;
  leftHandMode?: ILeftHandModeState;
  menu?: IMenuState;
  paytable?: IPaytableState;
  playForFunIndicator?: IPlayForFunIndicatorState;
  plugin?: IPluginState;
  quickSpin?: IQuickSpinState;
  settings?: ISettingsState;
  spin?: ISpinState;
  theme?: IThemeState;
  ticker?: ITickerState;
  totalWin?: ITotalWinState;
  volume?: IVolumeState;
  win?: IWinState;
  uiBlocker?: IUiBlockerState;
}

export interface ILanguageOptions {
  AutoplaySettingsSubTitle: string;
  AutoplaySettingsTitle: string;
  AutoplaySubTitle: string;
  AutoplayTitle: string;
  Balance: string;
  Bet: string;
  BetSettingsCashTitle: string;
  BetSettingsCoinsTitle: string;
  Coins: string;
  CoinValue: string;
  FreeRoundsLeft: string;
  FreeSpins: string;
  GameClientVersion: string;
  GameHistoryButton: string;
  GameHistoryDescriptionAvailable: string;
  GameHistoryDescriptionUnavailable: string;
  GameHistoryTitle: string;
  GameRulesButton: string;
  GameRulesDescription: string;
  GameRulesTitle: string;
  GameServerVersion: string;
  GameSettingsTitle: string;
  Level: string;
  MaxBet: string;
  MinBet: string;
  PaytableTitle: string;
  PlayingForFun: string;
  Required: string;
  Reset: string;
  TotalWin: string;
  Win: string;
}
